/**
 * Drives the Striker Run screen. The whole game loop lives inside the WebView runtime; this hook owns the
 * economy bridge: it fetches the day's economy state on `ready`, reports each finished run to the server,
 * and syncs coins from the SERVER response only (never a client-computed total). The server clamps + caps
 * every award (see server/src/constants/strikerRun), so a spoofed run-end can't mint coins.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useApiClient, extractErrorMessage } from '../../../helpers/apiClient.helper';
import { useEconomyStore } from '../../shared/stores/economyStore';
import { useAnalytics } from '../../analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../analytics/events';
import { startStrikerRun, recordStrikerRunEnd } from '../helpers/strikerRunApi';
import { buildStrikerRunGameHtml } from '../helpers/strikerRunGameHtml';
import {
  STRIKER_RUN_GAME_KEY,
  STRIKER_RUN_DAILY_COIN_CAP,
  type StrikerRunBridgeMessage,
} from '../constants/strikerRun.constants';

type Status = 'loading' | 'ready' | 'error';
const TOAST_MS = 2200;

/**
 * Intent-based host surface (not raw JS injection) so the same hook drives both the native WebView and the
 * web iframe, and stays unit-testable without either. Striker Run needs no config push — reload only.
 */
export interface StrikerRunWebViewRef {
  reload: () => void;
}

export function useStrikerRunGame() {
  const apiClient = useApiClient();
  const syncCoins = useEconomyStore((s) => s.syncCoins);
  const { track } = useAnalytics();

  const html = useMemo(() => buildStrikerRunGameHtml(), []);
  const webViewRef = useRef<StrikerRunWebViewRef | null>(null);
  const startedTrackedRef = useRef(false);

  const [status, setStatus] = useState<Status>('loading');
  const [coinsLeftToday, setCoinsLeftToday] = useState(STRIKER_RUN_DAILY_COIN_CAP);
  const [capReached, setCapReached] = useState(false);
  const [bestGoals, setBestGoals] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  // Session tally (this play, not all-time) — feeds the exit funnel signal.
  const [runsPlayed, setRunsPlayed] = useState(0);
  const [sessionCoins, setSessionCoins] = useState(0);
  const sessionBestRef = useRef(0);

  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const bindWebView = useCallback((ref: StrikerRunWebViewRef | null) => { webViewRef.current = ref; }, []);

  /** Fetch today's economy state. Called on `ready`; failure never blocks play (coins just won't bank). */
  const start = useCallback(async () => {
    try {
      const cfg = await startStrikerRun(apiClient);
      setCoinsLeftToday(cfg.coinsLeftToday);
      setCapReached(cfg.capReached);
      setBestGoals(cfg.bestGoals);
    } catch {
      // Offline fallback — the runtime is fully self-contained, so the game stays playable.
    }
  }, [apiClient]);

  const handleRunEnd = useCallback(async (msg: Extract<StrikerRunBridgeMessage, { type: 'run-end' }>) => {
    let coinsAwarded = 0; // stays 0 if the server call fails (offline / staging without routes)
    try {
      const res = await recordStrikerRunEnd(apiClient, { goals: msg.goals, pickups: msg.coins });
      coinsAwarded = res.coinsAwarded;
      // Coins come from the server only — anonymous users get no `coins`, so nothing is synced.
      if (typeof res.coins === 'number') syncCoins(res.coins);
      setCoinsLeftToday(res.coinsLeftToday);
      setCapReached(res.capReached);
      setBestGoals(res.bestGoals);
    } catch (err) {
      // Gameplay never blocks on the network — the runtime already offered the instant restart.
      if (msg.goals > 0) flashToast(extractErrorMessage(err, 'Coins will catch up'));
    } finally {
      setRunsPlayed((n) => n + 1);
      setSessionCoins((c) => c + coinsAwarded);
      sessionBestRef.current = Math.max(sessionBestRef.current, msg.goals);
      // Funnel signal — fire regardless of the server result so drop-off stays accurate.
      track(ANALYTICS_EVENTS.GAME_COMPLETED, {
        game_key: STRIKER_RUN_GAME_KEY,
        level: msg.goals,
        coins_awarded: coinsAwarded,
        reason: 'run-end',
        cause: msg.cause,
        distance: msg.distance,
        coins_collected: msg.coins,
      });
    }
  }, [apiClient, syncCoins, flashToast, track]);

  const onMessage = useCallback((event: { nativeEvent: { data: string } }) => {
    let msg: StrikerRunBridgeMessage;
    try { msg = JSON.parse(event.nativeEvent.data); } catch { return; }
    switch (msg.type) {
      case 'ready':
        setStatus('ready');
        if (!startedTrackedRef.current) {
          startedTrackedRef.current = true;
          track(ANALYTICS_EVENTS.GAME_STARTED, { game_key: STRIKER_RUN_GAME_KEY });
        }
        void start();
        break;
      case 'run-end':
        void handleRunEnd(msg);
        break;
      case 'load-error':
        setStatus('error');
        break;
      default:
        break;
    }
  }, [track, start, handleRunEnd]);

  const retry = useCallback(() => {
    setStatus('loading');
    webViewRef.current?.reload(); // runtime re-posts `ready` once rebooted
  }, []);

  /** Back pressed — close the session funnel; the view navigates away. */
  const confirmExit = useCallback(() => {
    track(ANALYTICS_EVENTS.GAME_COMPLETED, {
      game_key: STRIKER_RUN_GAME_KEY,
      level: sessionBestRef.current,
      coins_awarded: sessionCoins,
      reason: 'exit',
      runs_played: runsPlayed,
    });
  }, [track, sessionCoins, runsPlayed]);

  return {
    status,
    isLoading: status === 'loading',
    isError: status === 'error',
    html,
    coinsLeftToday,
    capReached,
    bestGoals,
    toast,
    runsPlayed,
    sessionCoins,
    bindWebView,
    onMessage,
    retry,
    confirmExit,
  };
}

export type UseStrikerRunGame = ReturnType<typeof useStrikerRunGame>;
